import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Bookmark, FileText, Wallet, Plus, ChevronRight,
  TrendingUp, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { StatusBadge } from "@/components/StatusBadge";
import { format } from "@/lib/brand-tokens";
import {
  DEMO_MEMBER, getMemberDashboardStats, getReservationsByMember, getDealById,
  getBrandById, getActiveDeals,
} from "@/lib/mock-data";

// MEM-01 — Member dashboard
export default function MemberDashboardPage() {
  const stats = getMemberDashboardStats(DEMO_MEMBER.id);
  const myActive = getReservationsByMember(DEMO_MEMBER.id)
    .filter((r) => r.status === "Active" || r.status === "SubmittedPartial")
    .slice(0, 3);
  const featured = getActiveDeals().slice(0, 3);
  const memberSince = new Date(DEMO_MEMBER.memberSince);
  const monthsActive = Math.max(1, Math.round((Date.now() - memberSince.getTime()) / (1000 * 60 * 60 * 24 * 30)));

  return (
    <div data-testid="member-dashboard-page" className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow text-brand-700">Welcome back</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-2 font-bold tracking-[-0.02em]">
            Hi, {DEMO_MEMBER.firstName}.
          </h1>
          <p className="text-body text-ink-muted mt-2">
            Member for <span className="text-ink font-semibold tabular-nums">{monthsActive}</span> months · <span className="text-ink font-semibold tabular-nums">{format.currency(DEMO_MEMBER.lifetimeEarnings)}</span> earned to date
          </p>
        </div>
        <Link to="/member/deals"
              className="group relative inline-flex items-center gap-3 h-12 pl-6 pr-2 rounded-full bg-brand-700 hover:bg-brand-800 text-white font-brand font-bold text-body-sm shadow-[0_8px_24px_rgba(15,118,110,0.25)] hover:shadow-[0_12px_32px_rgba(15,118,110,0.35)] hover:-translate-y-0.5 transition-all duration-300">
          <Plus className="size-4" /> <span>Reserve a deal</span>
          <span className="size-8 rounded-full bg-white/15 grid place-items-center transition-transform group-hover:rotate-45">
            <ArrowUpRight className="size-3.5" strokeWidth={2.4} />
          </span>
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MemberStat icon={<Bookmark className="size-4" />}
                    label="Active reservations" value={stats.activeReservationsCount}
                    sub={`across ${stats.activeReservationsAcrossDeals} deals`} link="/member/reservations" />
        <MemberStat icon={<Clock className="size-4" />}
                    label="Pending balance" value={format.currency(stats.pendingBalance)}
                    sub="approved · awaiting payout" link="/member/payments" />
        <MemberStat icon={<TrendingUp className="size-4" />}
                    label="Lifetime earnings" value={format.currency(stats.lifetimeEarnings)}
                    sub="completed payments" link="/member/payments" highlight />
        <MemberStat icon={<Wallet className="size-4" />}
                    label="Available capacity" value={format.currency(stats.availableCapacity)}
                    sub="remaining for new reservations" link="/member/deals" />
      </div>

      {/* Two-col body */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Active reservations */}
        <div className="lg:col-span-2 bg-surface border border-line rounded-xl">
          <div className="px-6 py-4 border-b border-line flex items-center justify-between">
            <h2 className="text-h3 text-ink font-semibold">Active reservations</h2>
            <Link to="/member/reservations" className="text-caption font-semibold text-brand-700 hover:text-brand-800">View all</Link>
          </div>
          {myActive.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-body-sm text-ink-muted">No active reservations.</div>
              <Link to="/member/deals"><Button variant="outline" size="sm" className="mt-3">Browse deals</Button></Link>
            </div>
          ) : (
            <ul className="divide-y divide-line/60">
              {myActive.map((r) => {
                const deal = getDealById(r.dealId);
                // Each reservation is scoped to a specific brand even on multi-brand deals.
                const brand = getBrandById(r.brandId || deal?.brandId);
                const days = Math.max(0, Math.ceil((new Date(deal?.deadline) - Date.now()) / 86_400_000));
                return (
                  <li key={r.id}>
                    <Link to={`/member/reservations/${r.id}`} className="flex items-center gap-4 px-6 py-4 hover:bg-surface-alt/60 transition-colors">
                      <BrandLogo brand={brand} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span className="text-body-sm font-semibold text-ink truncate max-w-[280px]">{deal?.name}</span>
                          <StatusBadge entity="reservation" status={r.status} />
                        </div>
                        <div className="text-caption text-ink-subtle truncate">{brand?.name} · {days} days left</div>
                      </div>
                      <div className="text-right">
                        <div className="text-body-sm font-semibold text-ink tabular-nums">{format.currency(r.faceValueReserved)}</div>
                        <div className="text-caption text-ink-subtle">Expected {format.currency(r.expectedPayout)}</div>
                      </div>
                      <ChevronRight className="size-4 text-ink-hint" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Activity */}
        <div className="bg-surface border border-line rounded-xl">
          <div className="px-5 py-4 border-b border-line">
            <h2 className="text-h3 text-ink font-semibold">Recent activity</h2>
          </div>
          <ul className="divide-y divide-line/60">
            {stats.recentActivity.slice(0, 6).map((a, i) => (
              <li key={i} className="px-5 py-3.5 flex items-start gap-3">
                <ActivityDot type={a.type} />
                <div className="flex-1 min-w-0">
                  <div className="text-body-sm text-ink truncate">{a.description}</div>
                  <div className="text-caption text-ink-subtle mt-0.5">{format.relative(a.at)}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Featured deals */}
      <section className="bg-surface border border-line rounded-xl p-6">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="eyebrow">For you</div>
            <h2 className="font-brand text-h2 text-ink mt-1">Featured deals this week</h2>
          </div>
          <Link to="/member/deals" className="text-caption font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
            Browse all <ArrowUpRight className="size-3.5" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {featured.map((deal) => {
            // Derive brand names from brandConfigs (multi-brand-aware). Falls
            // back to the legacy single brandId for older deal records.
            const cfgs = deal.brandConfigs || [];
            const brandList = cfgs.length > 0
              ? cfgs.map(bc => getBrandById(bc.brandId)).filter(Boolean)
              : [getBrandById(deal.brandId)].filter(Boolean);
            const primary = brandList[0];
            const brandSubtitle = brandList.length === 0 ? "—"
              : brandList.length === 1 ? brandList[0].name
              : `${brandList[0].name} +${brandList.length - 1} more`;
            // Best rate across all brand configs (or fall back to deal.rate).
            const bestRate = cfgs.length > 0
              ? Math.max(...cfgs.map(bc => bc.rate || 0))
              : deal.rate;
            const days = Math.max(0, Math.ceil((new Date(deal.deadline) - Date.now()) / 86_400_000));
            const remaining = deal.capTotal - deal.capUsed;
            return (
              <Link key={deal.id} to={`/member/deals/${deal.id}`}
                    className="group block bg-surface-alt border border-line rounded-lg p-5 hover:border-brand-700 hover:bg-surface transition-all">
                <div className="flex items-start justify-between gap-3">
                  <BrandLogo brand={primary} size="sm" />
                  <span className="font-brand text-h2 text-brand-700 tabular-nums">{bestRate}%</span>
                </div>
                <div className="mt-3 text-body-sm font-semibold text-ink line-clamp-2">{deal.name}</div>
                <div className="text-caption text-ink-subtle truncate mt-0.5">{brandSubtitle}</div>
                <div className="mt-4 pt-3 border-t border-line/60 flex items-center justify-between text-caption">
                  <span className="text-ink-muted tabular-nums">{format.currency(remaining)} left</span>
                  <span className="text-ink-muted tabular-nums">{days}d left</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function MemberStat({ icon, label, value, sub, link, highlight = false }) {
  return (
    <Link to={link} className={`group relative overflow-hidden block bg-surface border border-line rounded-2xl p-6 hover:shadow-[0_24px_48px_-16px_rgba(15,23,42,0.12)] hover:-translate-y-1 hover:border-ink/20 transition-all ${highlight ? "ring-1 ring-brand-700/10" : ""}`}>
      {highlight && (
        <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full pointer-events-none"
             style={{ background: "radial-gradient(closest-side, rgba(15,118,110,0.10), transparent)" }} />
      )}
      <div className="relative flex items-center justify-between">
        <div className="size-9 rounded-xl bg-gradient-to-br from-brand-50 to-brand-100 text-brand-700 grid place-items-center group-hover:from-brand-100 group-hover:to-brand-200 transition-colors">{icon}</div>
        <ArrowUpRight className="size-4 text-ink-hint group-hover:text-brand-700 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
      </div>
      <div className={`relative mt-5 font-brand text-[32px] font-bold leading-none tabular-nums tracking-[-0.02em] ${highlight ? "text-brand-700" : "text-ink"}`}>{value}</div>
      <div className="relative mt-2 text-body-sm text-ink font-semibold">{label}</div>
      <div className="relative mt-0.5 text-caption text-ink-subtle truncate">{sub}</div>
    </Link>
  );
}

function ActivityDot({ type }) {
  const map = { reservation: "bg-brand-700", submission: "bg-ink", payment: "bg-brand-300 ring-1 ring-brand-700/30" };
  return <span className={`size-2 rounded-full mt-1.5 shrink-0 ${map[type] || "bg-slate-400"}`} />;
}
